// client/src/components/bank/SubmitVatCreditBid.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { parseCurrencyInput, handleCurrencyInputChange, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { sanitizeInput } from '../utils/sanitize';
import { Edit } from 'lucide-react';
import VatCreditAttachmentList from '../vatCredit/VatCreditAttachmentList';

const SubmitVatCreditBid = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [request, setRequest] = useState(null);
  const [formData, setFormData] = useState({
    rateType: '',
    baseRate: '',
    interestRate: '',
    commission: '',
    indicativeDays: '',
    desiredAmount: '',
    transferNotification: '',
    notes: ''
  });
  const [maxAmount, setMaxAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bidSubmitted, setBidSubmitted] = useState(false);

  // Helper function to parse interest rate supporting both comma and dot
  const parseInterestRate = (value) => {
    if (value === null || value === undefined || value === '') return NaN;

    const normalizedValue = value.toString().trim().replace(',', '.');
    return parseFloat(normalizedValue);
  };

  // Base rate options: solo tassi EUR (il prodotto Crediti IVA è sempre in EUR)
  const getBaseRateOptions = () => ([
    { value: 'euribor_1w', label: t('bids.euribor1Week') },
    { value: 'euribor_1m', label: t('bids.euribor1Month') },
    { value: 'euribor_3m', label: t('bids.euribor3Months') },
    { value: 'euribor_6m', label: t('bids.euribor6Months') },
    { value: 'euribor_12m', label: t('bids.euribor12Months') },
    { value: 'estr', label: t('bids.estr') },
    { value: 'bce_dfr', label: t('bids.bceDfr') }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestRes = await axios.get(`/api/vat-credit-requests/${id}`);
        setRequest(requestRes.data.request);
        setMaxAmount(requestRes.data.request.amount);
        setLoading(false);
      } catch (err) {
        setError(t('common.errors.fetchRequestDetails'));
        setLoading(false);
      }
    };

    fetchData();
  }, [id, t]);

  const onChange = e => {
    if (e.target.name === 'desiredAmount') {
      handleCurrencyInputChange(e, setFormData, formData, 'desiredAmount');
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    // Clear error message when user starts typing again
    if (error) {
      setError('');
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    if (formData.rateType !== 'fixed' && formData.rateType !== 'floating') {
      setError(t('common.errors.selectRateType'));
      setSubmitting(false);
      return;
    }

    if (formData.rateType === 'floating' && !formData.baseRate) {
      setError(t('common.errors.selectBaseRate'));
      setSubmitting(false);
      return;
    }

    const interestRateValue = parseInterestRate(formData.interestRate);
    if (isNaN(interestRateValue)) {
      setError(t('common.errors.validInterestRate'));
      setSubmitting(false);
      return;
    }

    const commissionValue = parseInterestRate(formData.commission);
    if (isNaN(commissionValue) || commissionValue < 0) {
      setError(t('vatCredit.validation.validCommission'));
      setSubmitting(false);
      return;
    }

    const parsedIndicativeDays = parseInt(formData.indicativeDays, 10);
    if (isNaN(parsedIndicativeDays) || parsedIndicativeDays < 0) {
      setError(t('vatCredit.validation.validIndicativeDays'));
      setSubmitting(false);
      return;
    }

    if (formData.transferNotification !== 'notified' && formData.transferNotification !== 'not_notified') {
      setError(t('vatCredit.validation.selectTransferNotification'));
      setSubmitting(false);
      return;
    }

    const desiredAmount = parseCurrencyInput(formData.desiredAmount);
    if (isNaN(desiredAmount) || desiredAmount <= 0) {
      setError(t('common.errors.validAmount'));
      setSubmitting(false);
      return;
    }

    if (desiredAmount > maxAmount) {
      setError(t('common.errors.amountExceedsMax', {
        maxAmount: formatCurrencyWithSymbol(maxAmount, request.currency || 'EUR')
      }));
      setSubmitting(false);
      return;
    }

    try {
      const bidData = {
        requestId: request.id || request._id,
        rateType: formData.rateType,
        baseRate: formData.rateType === 'floating' ? formData.baseRate : null,
        interestRate: interestRateValue,
        commission: commissionValue,
        indicativeDays: parsedIndicativeDays,
        desiredAmount,
        transferNotification: formData.transferNotification,
        notes: sanitizeInput(formData.notes)
      };

      await axios.post('/api/vat-credit-bids', bidData);

      setBidSubmitted(true);

      setTimeout(() => {
        navigate('/bank/vat-credit/past-bids', {
          state: {
            bidSubmitted: true,
            message: t('requests.bidSuccessSubmitted'),
            activeTab: 'pending'
          }
        });
      }, 2500);

    } catch (err) {
      console.error('Error submitting VAT credit bid:', err);
      setError(err.response?.data?.message || t('common.errors.submitBid'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loadingRequestDetails')}</div>;
  }

  if (!request) {
    return <div className="error-message">{error || t('common.requestNotFound')}</div>;
  }

  if (bidSubmitted) {
    return (
      <div className="card">
        <div className="alert-success" style={{ padding: '20px', margin: '20px 0' }}>
          <h3>{t('requests.bidSuccessSubmitted')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="company-dashboard">

      {/* Navigation button outside and above the card */}
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/bank/vat-credit/open-requests')}
        >
          ← {t('bids.backToNewBids')}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <Edit size={20} />
          <span>{t('vatCredit.requestDetails')}</span>
          <div>
            <span className="status status-open">{t('status.open')}</span>
          </div>
        </div>

        <div className="request-info">
          {/* Horizontal info box - same style as BidDetail */}
          <div className="allocation-details-card">
            <div style={{ display: 'flex', gap: '40px', justifyContent: 'center' }}>
              <div>
                <strong>{t('requests.requestId')}:</strong> #{request.requestId}
              </div>
              <div>
                <strong>{t('requests.company')}:</strong> {request.company?.name || t('bids.notAvailable')}
              </div>
              <div>
                <strong>{t('profile.vatNumber')}:</strong> {request.company?.vatNumber || t('profile.vatNumberNotSpecified')}
              </div>
              <div>
                <strong>{t('requests.datePosted')}:</strong> {new Date(request.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>

          <h3 style={{ marginTop: '16px' }}>{t('vatCredit.title')}</h3>

          <div className="detail-item">
            <div className="detail-label">{t('requests.currency')}:</div>
            <div className="detail-value">{request.currency || 'EUR'}</div>
          </div>

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.amount')}:</div>
            <div className="detail-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</div>
          </div>

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.indicativeDays')}:</div>
            <div className="detail-value">{request.indicativeDays} {t('common.days')}</div>
          </div>

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.bidDeadline')}:</div>
            <div className="detail-value">
              {new Date(request.bidDeadline).toLocaleDateString()}
              {' '}
              <span style={{ color: '#6c757d' }}>
                {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} CET
              </span>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-label">{t('vatCredit.executionDate')}:</div>
            <div className="detail-value">{new Date(request.executionDate).toLocaleDateString()}</div>
          </div>

          {/* Display company notes if available */}
          {request.notes && (
            <div className="detail-item">
              <div className="detail-label">{t('requests.companyNotes')}:</div>
              <div className="detail-value request-notes">{request.notes}</div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '16px' }}>
          <VatCreditAttachmentList
            requestId={request.id || request._id}
            attachments={request.attachments}
          />
        </div>

        <div className="bid-form-container">
          <h3 style={{
            color: 'var(--primary)',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '18px',
            textDecoration: 'underline'
          }}>
            {t('requests.submitYourBid')}
          </h3>

          {/* Display error message prominently above form */}
          {error && (
            <div className="alert alert-danger">{error}</div>
          )}

          <form id="vat-credit-bid-form" onSubmit={onSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="desiredAmount">{t('vatCredit.desiredAmount')} ({request.currency || 'EUR'})</label>
              <input
                type="text"
                id="desiredAmount"
                name="desiredAmount"
                placeholder={t('requests.enterBidAmount')}
                value={formData.desiredAmount}
                onChange={onChange}
                required
              />
              <small>{t('requests.maxAmount')}: {formatCurrencyWithSymbol(maxAmount, request.currency || 'EUR')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="rateType">{t('bids.selectRateType')} *</label>
              <select
                id="rateType"
                name="rateType"
                className="form-select-custom"
                value={formData.rateType}
                onChange={onChange}
                required
              >
                <option value="">{t('requests.selectRateType')}</option>
                <option value="fixed">{t('requests.fixed')}</option>
                <option value="floating">{t('requests.floating')}</option>
              </select>
            </div>

            {/* Base Rate Selection - Show for floating rate */}
            {formData.rateType === 'floating' && (
              <div className="form-group">
                <label htmlFor="baseRate">{t('bids.baseRate')} *</label>
                <select
                  id="baseRate"
                  name="baseRate"
                  className="form-select-custom"
                  value={formData.baseRate}
                  onChange={onChange}
                  required
                >
                  <option value="">{t('bids.selectBaseRate')}</option>
                  {getBaseRateOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small>{t('bids.baseRateDescription')}</small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="interestRate">
                {formData.rateType === 'floating'
                  ? t('bids.interestRateFloating')
                  : t('bids.interestRateFixed')}
              </label>
              <input
                type="number"
                id="interestRate"
                name="interestRate"
                placeholder={formData.rateType === 'floating'
                  ? t('requests.enterSpread')
                  : t('requests.enterInterestRate')}
                min="-2"
                step="0.01"
                value={formData.interestRate}
                onChange={onChange}
                onWheel={(e) => e.target.blur()}
                required
                disabled={!formData.rateType}
              />
            </div>

            <div className="form-group">
              <label htmlFor="commission">{t('vatCredit.commission')} (%) *</label>
              <input
                type="number"
                id="commission"
                name="commission"
                placeholder={t('vatCredit.enterCommission')}
                min="0"
                step="0.01"
                value={formData.commission}
                onChange={onChange}
                onWheel={(e) => e.target.blur()}
                required
              />
              <small>{t('vatCredit.commissionHelp')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="indicativeDays">{t('vatCredit.bankIndicativeDays')} *</label>
              <input
                type="number"
                id="indicativeDays"
                name="indicativeDays"
                placeholder={t('vatCredit.enterIndicativeDays')}
                min="0"
                step="1"
                value={formData.indicativeDays}
                onChange={onChange}
                onWheel={(e) => e.target.blur()}
                required
              />
              <small>{t('vatCredit.bankIndicativeDaysHelp')}</small>
            </div>

            <div className="form-group">
              <label htmlFor="transferNotification">{t('vatCredit.transferNotification')} *</label>
              <select
                id="transferNotification"
                name="transferNotification"
                className="form-select-custom"
                value={formData.transferNotification}
                onChange={onChange}
                required
              >
                <option value="">{t('vatCredit.selectTransferNotification')}</option>
                <option value="notified">{t('vatCredit.notified')}</option>
                <option value="not_notified">{t('vatCredit.notNotified')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="notes">{t('requests.additionalNotes')}</label>
              <textarea
                id="notes"
                name="notes"
                rows="4"
                placeholder={t('requests.additionalBidInfo')}
                value={formData.notes}
                onChange={onChange}
              ></textarea>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => navigate('/bank/vat-credit/open-requests')}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? t('actions.submitting') : t('bids.submitBid')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmitVatCreditBid;
